import { useEffect, useState, useCallback, useRef } from "react";

// Lightweight async state manager for the mock data layer.
// Exposes { data, loading, error, isEmpty, refetch } so every route can render
// polished loading / empty / error states consistently.
export function useMockQuery(fn, deps = [], { emptyCheck } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  const fnRef = useRef(fn);
  fnRef.current = fn;

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.resolve()
      .then(() => fnRef.current())
      .then((res) => {
        if (alive) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (alive) {
          setError(err);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const isEmpty =
    !loading &&
    !error &&
    (emptyCheck
      ? emptyCheck(data)
      : Array.isArray(data)
      ? data.length === 0
      : data == null);

  return { data, loading, error, isEmpty, refetch };
}

export default useMockQuery;
